"use client";

import type { ReactNode } from "react";
import { Suspense, useState } from "react";
import { usePathname } from "next/navigation";

import { BottomNav } from "@/components/BottomNav";
import { GlobalPreferencesBridge } from "@/components/GlobalPreferencesBridge";
import { TopBar } from "@/components/TopBar";
import { fetchJson } from "@/lib/api";

export function AppShell({ children }: { children: ReactNode }) {
  const [undoMessage, setUndoMessage] = useState<string>("");
  const pathname = usePathname();
  const isPublic = pathname === "/" || pathname === "/login" || pathname === "/signup" || pathname === "/onboarding" || pathname === "/terms" || pathname === "/privacy" || pathname.startsWith("/invite/");
  const isWorkspace = pathname.startsWith("/w/");
  const isUnifiedApp = pathname.startsWith("/app/");

  if (isWorkspace || isUnifiedApp) return <>{children}</>;

  async function undo() {
    try {
      const data = await fetchJson<{ ok: true; message: string }>("/api/operations/undo", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setUndoMessage(data.message);
    } catch (e) {
      setUndoMessage(e instanceof Error ? e.message : "取り消しに失敗しました");
    } finally {
      window.setTimeout(() => setUndoMessage(""), 3000);
    }
  }

  return (
    <div className={isPublic ? "min-h-dvh bg-white text-[color:var(--fg)]" : "flex h-dvh min-h-0 flex-col overflow-hidden bg-white text-[color:var(--fg)]"}>
      <GlobalPreferencesBridge />
      {isPublic ? null : <TopBar />}
      <main className={isPublic ? "mx-auto w-full max-w-[1520px] px-4 py-10 sm:px-6 animate-fade" : "min-h-0 flex-1 overflow-y-auto overscroll-contain w-full max-w-none px-0 pb-[calc(env(safe-area-inset-bottom)+4.5rem)] pt-0 animate-fade md:pb-14"}>
        {children}
      </main>
      {isPublic ? null : <Suspense fallback={null}><BottomNav /></Suspense>}

      {isPublic ? null : (
      <div className="fixed bottom-20 left-4 z-50 flex items-end gap-3 sm:bottom-6 sm:left-6">
        <button
          type="button"
          onClick={() => void undo()}
          className="button-secondary h-12 w-12 rounded-full bg-white text-lg shadow-[0_12px_24px_rgba(18,18,18,0.12)]"
          aria-label="取り消し"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 14l-4-4 4-4" />
            <path d="M5 10h8a6 6 0 0 1 0 12h-1" />
          </svg>
        </button>
        {undoMessage ? (
          <div role="status" aria-live="polite" className="surface-panel px-3 py-2 text-xs shadow-[0_12px_24px_rgba(18,18,18,0.08)]">
            {undoMessage}
          </div>
        ) : null}
      </div>
      )}
    </div>
  );
}
